import React, { useState, useEffect } from "react";
import { X, FileText, User, Phone, Zap, Loader, Search } from "lucide-react";
import {
  ICreateQuotationForm,
  IRegisteredUserWithDetails,
} from "../../models/epcCustomer.model";
import { getUser } from "../../utils/auth";
import Button, { ButtonType } from "../Assets/Button";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import { getCustomerByMobileNumber } from "../../store/slices/epcSlice";
import { usePreventWheelChange } from "../../utils/usePreventWheelChange";
import { useInfiniteSalesReps } from "../../hooks/useInfiniteSalesReps";
import Select from "react-select";

interface CreateQuotationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ICreateQuotationForm) => void;
  isLoading?: boolean;
  customer?: IRegisteredUserWithDetails | null;
  isDynamic?: boolean;
}

const CreateQuotationModal: React.FC<CreateQuotationModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  customer,
  isDynamic = false,
}) => {
  const dispatch: RootDispatch = useDispatch();

  const currentUser = getUser();
  const fetchedCustomer = useSelector(
    (state: RootState) => state.epc.customerByMobileNumber,
  );
  const isFetchingCustomer = useSelector(
    (state: RootState) =>
      state.epc.loadingState.fetchingCustomerByMobileNumberLoading,
  );

  const {
    salesReps: infiniteSalesReps,
    isLoadingMore,
    hasMore,
    loadMore,
    handleSearch,
  } = useInfiniteSalesReps(isOpen);

  const handleWheelPrevent = usePreventWheelChange();

  const getSalesRepId = () => {
    if (!customer) return undefined;

    if (
      typeof customer.salesRepresentative === "object" &&
      customer.salesRepresentative
    ) {
      return (customer.salesRepresentative as any)._id;
    }

    return customer.salesRepresentative as string;
  };

  // Helper function to get CSE ID from customer
  const getCseId = () => {
    if (!customer) return currentUser?.id;

    if (typeof customer.cseID === "object" && customer.cseID) {
      return (customer.cseID as any)._id;
    }

    return customer.cseID as string;
  };

  const [formData, setFormData] = useState<ICreateQuotationForm>(() => {
    // Initialize with customer data if available
    if (isDynamic && customer) {
      return {
        customerName: customer.customerName || "",
        mobileNumber: customer.mobileNumber || "",
        subsidyOption: "",
        subsidyAmount: 0,
        brandOption: "",
        customBrand: "",
        brand: "",
        panelType: "",
        plantSize: "",
        plantCost: undefined,
        meterCost: 0,
        netMeterOption: "",
        netMeterCost: undefined,
        salesRepresentative: getSalesRepId() || getCseId(),
        cseID: getCseId(),
      };
    }

    // Default empty state
    return {
      customerName: "",
      mobileNumber: "",
      subsidyOption: "",
      subsidyAmount: 0,
      brandOption: "",
      customBrand: "",
      brand: "",
      panelType: "",
      plantSize: "",
      plantCost: undefined,
      meterCost: 0,
      netMeterOption: "",
      netMeterCost: undefined,
      salesRepresentative: undefined,
      cseID: currentUser?.id || undefined,
    };
  });

  const [errors, setErrors] = useState<
    Partial<Record<keyof ICreateQuotationForm, string>>
  >({});

  const [mobileForFetch, setMobileForFetch] = useState("");

  useEffect(() => {
    if (fetchedCustomer && !isFetchingCustomer) {
      setFormData((prev) => ({
        ...prev,
        customerName: fetchedCustomer.customerName || "",
        mobileNumber: mobileForFetch || prev.mobileNumber,
        salesRepresentative:
          (fetchedCustomer.salesRepresentative as any)?._id ||
          (fetchedCustomer.cseID as any)?._id ||
          prev.salesRepresentative,
        cseID: (fetchedCustomer.cseID as any)?._id || prev.cseID,
      }));

      // Clear any mobile number error
      if (errors.mobileNumber) {
        setErrors((prev) => ({ ...prev, mobileNumber: "" }));
      }
    }
  }, [fetchedCustomer, isFetchingCustomer]);

  useEffect(() => {
    if (isOpen) {
      if (isDynamic && customer) {
        setFormData({
          customerName: customer.customerName || "",
          mobileNumber: customer.mobileNumber || "",
          subsidyOption: formData.subsidyOption || "",
          subsidyAmount: formData.subsidyAmount || 0,
          brandOption: formData.brandOption || "",
          customBrand: formData.customBrand || "",
          brand: formData.brand || "",
          panelType: formData.panelType || "",
          plantSize: formData.plantSize || "",
          plantCost: formData.plantCost,
          meterCost: formData.meterCost || 0,
          netMeterOption: formData.netMeterOption || "",
          netMeterCost: formData.netMeterCost,
          salesRepresentative: getSalesRepId() || getCseId(),
          cseID: getCseId(),
        });
      } else {
        // Reset to empty state when no customer
        setFormData({
          customerName: "",
          mobileNumber: "",
          subsidyOption: "",
          subsidyAmount: 0,
          brandOption: "",
          customBrand: "",
          brand: "",
          panelType: "",
          plantSize: "",
          plantCost: undefined,
          meterCost: 0,
          netMeterOption: "",
          netMeterCost: undefined,
          salesRepresentative: undefined,
          cseID: currentUser?.id || undefined,
        });
      }
      setErrors({});
    }
  }, [isOpen, customer, isDynamic]);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof ICreateQuotationForm, string>> = {};

    if (!formData.customerName.trim()) {
      newErrors.customerName = "Customer name is required";
    }

    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = "Mobile number is required";
    } else if (!/^\d{10}$/.test(formData.mobileNumber)) {
      newErrors.mobileNumber = "Mobile number must be 10 digits";
    }

    if (!formData.salesRepresentative && !formData.cseID) {
      newErrors.salesRepresentative =
        "Sales representative is required or CSE  is required";
    }

    if (!formData.brand.trim()) {
      newErrors.brand = "Brand is required";
    }

    if (!formData.panelType.trim()) {
      newErrors.panelType = "Panel type is required";
    }

    if (!formData.plantSize.trim()) {
      newErrors.plantSize = "Plant size is required";
    }

    if (formData.plantCost! <= 0) {
      newErrors.plantCost = "Plant cost must be greater than 0";
    }

    if (formData.meterCost < 0) {
      newErrors.meterCost = "Meter cost cannot be negative";
    }

    if (formData.netMeterCost! < 0) {
      newErrors.netMeterCost = "Net meter cost cannot be negative";
    }

    if (formData.subsidyAmount < 0) {
      newErrors.subsidyAmount = "Subsidy amount cannot be negative";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.MouseEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const submissionData: ICreateQuotationForm = {
        ...formData,
        salesRepresentative: formData.salesRepresentative,
      };

      onSubmit(submissionData);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      let newData: any = { ...prev };

      if (name === "brandOption") {
        newData.brandOption = value;
        newData.brand = value !== "other" ? value : prev.customBrand;
      } else if (name === "customBrand") {
        newData.customBrand = value;
        if (prev.brandOption === "other") {
          newData.brand = value;
        }
      } else if (name === "mobileNumber") {
        setMobileForFetch(value);
        newData[name] = value;
      } else {
        newData[name] = [
          "subsidyAmount",
          "plantCost",
          "plantCost2",
          "meterCost",
          "netMeterCost",
        ].includes(name)
          ? parseFloat(value)
          : value;
      }

      // clear error if exists
      if (errors[name as keyof ICreateQuotationForm]) {
        setErrors((prev) => ({ ...prev, [name]: "" }));
      }

      return newData;
    });
  };

  const handleFetchCustomer = async () => {
    if (!mobileForFetch || !/^\d{10}$/.test(mobileForFetch)) {
      setErrors((prev) => ({
        ...prev,
        mobileNumber: "Please enter a valid 10-digit mobile number",
      }));
      return;
    }

    await dispatch(getCustomerByMobileNumber(mobileForFetch));
  };

  const closeModal = () => {
    onClose();
    setFormData({
      customerName: "",
      mobileNumber: "",
      subsidyOption: "",
      subsidyAmount: 0,
      brandOption: "",
      customBrand: "",
      brand: "",
      panelType: "",
      plantSize: "",
      plantCost: undefined,
      meterCost: 0,
      netMeterOption: "",
      netMeterCost: undefined,
      salesRepresentative: undefined,
      cseID: currentUser?.id || undefined,
    });
  };

  const salesRepOptions = [
    {
      value: currentUser?.id || "",
      label: `Self - ${currentUser?.username}`,
    },
    ...infiniteSalesReps.map((rep: any) => ({
      value: rep._id,
      label: `${rep.name} (${rep.mobileNumber})`,
    })),
  ];

  const selectedSalesRep = salesRepOptions.find(
    (opt) =>
      opt.value ===
      (formData.salesRepresentative
        ? formData.salesRepresentative
        : formData.cseID),
  );

  const MenuList = (props: any) => {
    const { children } = props;

    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.target as HTMLDivElement;
      const bottom =
        target.scrollHeight - target.scrollTop <= target.clientHeight + 5;

      if (bottom && hasMore && !isLoadingMore) {
        loadMore();
      }
    };

    return (
      <div
        onScroll={handleScroll}
        style={{ maxHeight: "300px", overflowY: "auto" }}
      >
        {children}
        {isLoadingMore && (
          <div className="flex justify-center items-center p-3">
            <Loader className="w-4 h-4 animate-spin text-gray-400" />
            <span className="ml-2 text-xs text-gray-500">Loading more...</span>
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  const panelTypes = ["Bifacial (Mono PERC)", "Topcon"];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[150] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gray-100 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 flex items-center justify-center">
              <FileText className="w-5 h-5 text-gray-800" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Create Quotation
              </h2>
              <p className="text-gray-500 text-sm">
                Generate a new solar quotation
              </p>
            </div>
          </div>
          <button
            onClick={closeModal}
            disabled={isLoading}
            className="text-gray-600 hover:text-gray-900 transition-colors p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Customer Information */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <User className="w-4 h-4" />
                Customer Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Customer Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="customerName"
                    value={formData.customerName}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all text-sm ${
                      errors.customerName
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    }`}
                    placeholder="Enter customer name"
                  />
                  {errors.customerName && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.customerName}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Customer Mobile Number{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <div
                      className={`relative ${!isDynamic ? "flex-1" : "w-full"}`}
                    >
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <span className="absolute left-10 top-1/2 transform -translate-y-1/2 text-gray-700 font-medium text-sm">
                        +91
                      </span>
                      <input
                        type="tel"
                        name="mobileNumber"
                        disabled={isDynamic || isFetchingCustomer}
                        value={formData.mobileNumber}
                        onChange={handleChange}
                        maxLength={10}
                        className={`w-full pl-20 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all text-sm ${
                          errors.mobileNumber
                            ? "border-red-300 bg-red-50"
                            : "border-gray-300"
                        }`}
                        placeholder="10-digit mobile number"
                      />
                    </div>
                    <div>
                      {!isDynamic && (
                        <Button
                          type={ButtonType.Extra}
                          onClick={handleFetchCustomer}
                          disabled={
                            isFetchingCustomer ||
                            !formData.mobileNumber ||
                            formData.mobileNumber.length !== 10
                          }
                          className="flex items-center gap-2 px-4 py-2 text-sm whitespace-nowrap justify-center"
                        >
                          {isFetchingCustomer ? (
                            <>
                              <Loader className="w-4 h-4 animate-spin" />
                            </>
                          ) : (
                            <>
                              <Search className="w-4 h-4" />
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                  {errors.mobileNumber && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.mobileNumber}
                    </p>
                  )}
                </div>
                <div className="md:col-span-2 text-sm">
                  <label className="block font-medium text-gray-700 mb-1.5">
                    Sales Representative <span className="text-red-500">*</span>
                  </label>

                  <Select
                    options={salesRepOptions}
                    value={selectedSalesRep || null}
                    isDisabled={!!customer || isLoading}
                    placeholder="Select Sales Representative..."
                    isClearable
                    components={{ MenuList }}
                    onChange={(selectedOption: { value: string } | null) => {
                      const selectedValue = selectedOption?.value;

                      if (selectedValue === currentUser?.id) {
                        setFormData((prev) => ({
                          ...prev,
                          cseID: currentUser?.id || "",
                          salesRepresentative: undefined,
                        }));
                      } else {
                        setFormData((prev) => ({
                          ...prev,
                          salesRepresentative: selectedValue,
                          cseID: undefined,
                        }));
                      }

                      if (errors.salesRepresentative) {
                        setErrors((prev) => ({
                          ...prev,
                          salesRepresentative: "",
                        }));
                      }
                    }}
                    onInputChange={(inputValue, actionMeta) => {
                      if (actionMeta.action === "input-change") {
                        handleSearch(inputValue);
                      }
                    }}
                    styles={{
                      control: (base: any) => ({
                        ...base,
                        minHeight: "40px",
                        borderColor: errors.salesRepresentative
                          ? "#fca5a5"
                          : "#d1d5db",
                        boxShadow: "none",
                        "&:hover": {
                          borderColor: errors.salesRepresentative
                            ? "#f87171"
                            : "#9ca3af",
                        },
                      }),
                      menu: (base: any) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                    }}
                  />

                  {errors.salesRepresentative && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.salesRepresentative}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Solar System Details */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <Zap className="w-4 h-4" />
                Solar System Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Solar Panel Brand <span className="text-red-500">*</span>
                  </label>

                  <select
                    name="brandOption"
                    value={formData.brandOption}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all text-sm ${
                      errors.brand
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    }`}
                  >
                    <option value="">Select brand</option>
                    <option value="Adani">Adani</option>
                    <option value="Waaree">Waaree</option>
                    <option value="other">Other</option>
                  </select>

                  {/* Show custom input if "Other" is selected */}
                  {formData.brandOption === "other" && (
                    <div className="relative mt-2">
                      <input
                        type="text"
                        name="customBrand"
                        value={formData.customBrand}
                        onChange={handleChange}
                        placeholder="Enter custom brand"
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all text-sm ${
                          errors.brand
                            ? "border-red-300 bg-red-50"
                            : "border-gray-300"
                        }`}
                      />
                    </div>
                  )}

                  {errors.brand && (
                    <p className="text-red-500 text-xs mt-1">{errors.brand}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Panel Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="panelType"
                    value={formData.panelType}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all text-sm ${
                      errors.panelType
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    }`}
                  >
                    <option value="">Select panel type</option>
                    {panelTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  {errors.panelType && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.panelType}
                    </p>
                  )}
                </div>
                {/* Net Meter Cost */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Net Meter Cost (₹)
                  </label>

                  <select
                    name="netMeterOption"
                    value={formData.netMeterOption}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        netMeterOption: value,
                        netMeterCost:
                          value === "asPerActual" ? 0 : prev.netMeterCost,
                      }));
                      if (errors.netMeterCost)
                        setErrors((prev) => ({ ...prev, netMeterCost: "" }));
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all text-sm ${
                      errors.netMeterCost
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    }`}
                  >
                    <option value="">Select option</option>
                    <option value="asPerActual">As per actual</option>
                    <option value="enterPrice">Enter price</option>
                  </select>

                  {/* Show input only if "Enter price" is selected */}
                  {formData.netMeterOption === "enterPrice" && (
                    <div className="relative mt-2">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                        ₹
                      </span>
                      <input
                        type="number"
                        name="netMeterCost"
                        value={formData.netMeterCost}
                        onChange={handleChange}
                        min="0"
                        step="1000"
                        className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all text-sm ${
                          errors.netMeterCost
                            ? "border-red-300 bg-red-50"
                            : "border-gray-300"
                        }`}
                        placeholder="Enter net meter cost"
                      />
                    </div>
                  )}

                  {errors.netMeterCost && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.netMeterCost}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Plant Cost (₹) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                      ₹
                    </span>
                    <input
                      type="number"
                      name="plantCost"
                      value={formData.plantCost}
                      onWheel={handleWheelPrevent}
                      onChange={handleChange}
                      step="1000"
                      className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all text-sm ${
                        errors.plantCost
                          ? "border-red-300 bg-red-50"
                          : "border-gray-300"
                      }`}
                      placeholder="Enter plant cost"
                    />
                  </div>
                  {errors.plantCost && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.plantCost}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Plant Size <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="plantSize"
                    value={formData.plantSize}
                    onChange={handleChange}
                    onWheel={handleWheelPrevent}
                    placeholder="Enter plant size"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all text-sm ${
                      errors.plantSize
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    }`}
                  />
                  {errors.plantSize && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.plantSize}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Subsidy Amount (₹)
                  </label>

                  <select
                    name="subsidyOption"
                    value={formData.subsidyOption}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        subsidyOption: value,
                        subsidyAmount:
                          value !== "other" && value ? Number(value) : 0,
                      }));
                      if (errors.subsidyAmount)
                        setErrors((prev) => ({ ...prev, subsidyAmount: "" }));
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all text-sm ${
                      errors.subsidyAmount
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    }`}
                  >
                    <option value="">Select amount</option>
                    <option value="78000">78000</option>
                    <option value="other">Other</option>
                  </select>

                  {/* Show input only if "Other" is selected */}
                  {formData.subsidyOption === "other" && (
                    <div className="relative mt-2">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                        ₹
                      </span>
                      <input
                        type="number"
                        name="subsidyAmount"
                        value={formData.subsidyAmount}
                        onChange={handleChange}
                        min="0"
                        step="1000"
                        className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all text-sm ${
                          errors.subsidyAmount
                            ? "border-red-300 bg-red-50"
                            : "border-gray-300"
                        }`}
                        placeholder="Enter amount"
                      />
                    </div>
                  )}

                  {errors.subsidyAmount && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.subsidyAmount}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-100 border-t border-gray-200 flex gap-3 justify-end">
            <Button
              type={ButtonType.Cancel}
              onClick={closeModal}
              disabled={isLoading}
              className="text-xs"
            >
              Cancel
            </Button>
            <Button
              type={ButtonType.Submit}
              onClick={handleSubmit}
              disabled={isLoading}
              className="text-xs inline-flex items-center gap-1"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4" />
                  Create Quotation
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateQuotationModal;
